/* ============================================================
   TABBED PHOTOS PRODUCT PAGE

   Client-side variant resolution from the matrix the section emits.
   No section re-render, no fetch, no Section Rendering API round-trip:
   a tab click swaps attributes and an image source, and that is all.

   This is the deliberate replacement for the Dawn-derived first attempt,
   where <variant-selects> was hoisted around the whole panel. Dawn's
   HTMLUpdateUtility.viewTransition REPLACES that element on every variant
   change — inserting a clone, hiding the original, removing it 500ms
   later — so hoisting it cloned the image, the gallery and the buy column
   on every click, stacked duplicate listeners and cascaded into 67 section
   renders per interaction. Nothing here can do that: there is one element,
   it is never replaced, and there is exactly one listener per control.

   Add-to-cart is still Shopify's: <product-form> owns the AJAX submit and
   the cart drawer, and reads the hidden [name=id] input this element keeps
   up to date. Price, stock and edition data all come from the matrix,
   which comes from Liquid — none of it is hard-coded.
   ============================================================ */

class TabbedPhotosPage extends HTMLElement {
  connectedCallback() {
    const script = this.querySelector('[data-tpp-matrix]');
    if (!script) return;

    try {
      this.data = JSON.parse(script.textContent);
    } catch (err) {
      console.error('[mom-product-photo] variant matrix is not valid JSON', err);
      return;
    }

    this.photoFormats = (this.dataset.photoFormats || '')
      .split('|')
      .map((s) => s.trim())
      .filter(Boolean);

    this.tabs = Array.from(this.querySelectorAll('[data-tpp-tab]'));
    this.optionButtons = Array.from(this.querySelectorAll('[data-tpp-option]'));
    this.optionGroups = Array.from(this.querySelectorAll('[data-tpp-optgroup]'));
    this.image = this.querySelector('[data-tpp-image]');
    this.thumbsHost = this.querySelector('[data-tpp-thumbs]');
    this.priceNode = this.querySelector('[data-tpp-price]');
    this.variantInput = this.querySelector('[data-tpp-variant-input]');
    this.productForm = this.querySelector('product-form');
    this.closedNotice = this.querySelector('[data-tpp-closed]');
    this.submit = this.querySelector('[data-tpp-submit]');

    this.assertNeverSoldOut();
    this.bind();
    this.selectInitial();
  }

  /* ---- invariant ---------------------------------------------
     Loxley photograph products are built to order and are never sold
     out: inventory is a device for Shopify's shipping rules, not a
     stock signal. While that holds, "no variant for this combination"
     is the ONLY reason an option is hidden, which is what makes hiding
     it correct rather than a trade-off. If the invariant ever breaks,
     a genuinely sold-out option would be hidden instead of shown — so
     complain loudly rather than mis-sell quietly.
     ------------------------------------------------------------ */
  assertNeverSoldOut() {
    const unavailable = this.data.variants.filter((v) => v.available === false);
    if (!unavailable.length) return;

    console.error(
      `[mom-product-photo] INVARIANT BROKEN: ${unavailable.length} of ` +
        `${this.data.variants.length} variants report available:false. Photograph ` +
        `products are built to order and should never be sold out. While the ` +
        `invariant holds, a hidden option means "no such combination"; with real ` +
        `sold-out variants present, a sold-out option is now hidden rather than ` +
        `shown. Check inventory_policy on this product.`,
      unavailable
    );

    if (window.Shopify && window.Shopify.designMode) {
      const note = document.createElement('p');
      note.className = 'tpp__invariant-warning';
      note.textContent =
        'Theme editor notice: this product has variants marked unavailable. ' +
        'Photograph products are built to order and should never be sold out — ' +
        'check the inventory policy before publishing.';
      this.prepend(note);
    }
  }

  /* ---- wiring ------------------------------------------------ */

  bind() {
    this.tabs.forEach((tab, i) => {
      tab.addEventListener('click', () => this.selectFormat(tab.dataset.format));
      tab.addEventListener('keydown', (e) => this.onTabKey(e, i));
    });

    this.optionButtons.forEach((btn) => {
      btn.addEventListener('click', () => {
        this.state[btn.dataset.position] = btn.dataset.value;
        this.reconcileFrom(Number(btn.dataset.position));
        this.render();
      });
    });

    this.querySelectorAll('[data-tpp-qty]').forEach((btn) => {
      btn.addEventListener('click', () => {
        const input = this.querySelector('[data-tpp-qtyinput]');
        if (!input) return;
        const next = Math.max(1, (parseInt(input.value, 10) || 1) + Number(btn.dataset.tppQty));
        input.value = next;
      });
    });
  }

  /** Roving tabindex — arrow keys move between tabs, as a tablist should. */
  onTabKey(event, index) {
    const keys = { ArrowRight: 1, ArrowDown: 1, ArrowLeft: -1, ArrowUp: -1 };
    let next = null;

    if (event.key in keys) next = (index + keys[event.key] + this.tabs.length) % this.tabs.length;
    else if (event.key === 'Home') next = 0;
    else if (event.key === 'End') next = this.tabs.length - 1;
    if (next === null) return;

    event.preventDefault();
    this.selectFormat(this.tabs[next].dataset.format);
    this.tabs[next].focus();
  }

  /* ---- selection --------------------------------------------- */

  /** Open on the variant Shopify resolved server-side, so ?variant= is honoured. */
  selectInitial() {
    const initial =
      this.data.variants.find((v) => v.id === this.data.selectedId) || this.data.variants[0];
    if (!initial) return;

    this.state = {};
    initial.options.forEach((value, i) => {
      this.state[i + 1] = value;
    });
    this.render();
  }

  selectFormat(format) {
    if (!format || this.state[1] === format) return;
    this.state[1] = format;
    this.reconcileFrom(1);
    this.render();
  }

  /** Variants matching the chosen values at positions 1..upTo. */
  matching(upTo) {
    return this.data.variants.filter((v) =>
      v.options.every((value, i) => i + 1 > upTo || this.state[i + 1] === value)
    );
  }

  /** Distinct values available at `position`, given everything chosen above it. */
  valuesAt(position) {
    const pool = this.matching(position - 1);
    const seen = [];
    pool.forEach((v) => {
      const value = v.options[position - 1];
      if (value !== undefined && !seen.includes(value)) seen.push(value);
    });
    return seen;
  }

  /**
   * After a change at `position`, walk every option below it and keep the
   * current value where it is still valid, otherwise take the first that is.
   * Keeping the choice matters: switching format should not silently reset
   * a size the customer already picked when that size still exists.
   */
  reconcileFrom(position) {
    const count = this.data.optionNames.length;
    for (let p = position + 1; p <= count; p += 1) {
      const valid = this.valuesAt(p);
      if (!valid.length) {
        delete this.state[p];
      } else if (!valid.includes(this.state[p])) {
        this.state[p] = valid[0];
      }
    }
  }

  currentVariant() {
    return (
      this.data.variants.find((v) => v.options.every((value, i) => this.state[i + 1] === value)) ||
      null
    );
  }

  /**
   * The shortened name to DISPLAY for a format. `formatLabels` is parallel to
   * `formats`, index for index. Everything else — matching, filtering, the
   * variant sent to the cart — uses the raw option value, so shortening a
   * label can never change what is bought.
   */
  labelFor(format) {
    const i = this.data.formats.indexOf(format);
    return (this.data.formatLabels && this.data.formatLabels[i]) || format || '';
  }

  /* ---- render ------------------------------------------------ */

  render() {
    const format = this.state[1];
    const variant = this.currentVariant();

    this.renderTabs(format);
    this.renderOptions();
    this.renderMedia(format, variant);
    this.renderPurchase(format, variant);
  }

  renderTabs(format) {
    this.tabs.forEach((tab) => {
      const isSelected = tab.dataset.format === format;
      tab.setAttribute('aria-selected', isSelected ? 'true' : 'false');
      tab.tabIndex = isSelected ? 0 : -1;

      const pool = this.data.variants.filter(
        (v) => v.options[0] === tab.dataset.format && v.available !== false
      );
      const from = tab.querySelector('[data-tpp-tab-from]');
      if (!from) return;

      if (!pool.length) {
        tab.dataset.unavailable = 'true';
        from.textContent = 'Unavailable';
        return;
      }
      delete tab.dataset.unavailable;
      const cheapest = pool.reduce((a, b) => (a.price <= b.price ? a : b));
      // priceFormatted comes from Liquid's `money` filter and may contain an
      // HTML entity for the currency symbol, so it is written as markup.
      from.innerHTML = `From ${cheapest.priceFormatted}`;
    });
  }

  renderOptions() {
    const count = this.data.optionNames.length;

    for (let p = 2; p <= count; p += 1) {
      const valid = this.valuesAt(p);
      const group = this.optionGroups.find((g) => Number(g.dataset.position) === p);
      if (group) group.hidden = valid.length === 0;

      this.optionButtons
        .filter((b) => Number(b.dataset.position) === p)
        .forEach((btn) => {
          // A value that cannot combine with the chosen format is not a
          // choice the customer can make, so it is hidden, not disabled.
          btn.hidden = !valid.includes(btn.dataset.value);
          btn.setAttribute(
            'aria-pressed',
            this.state[p] === btn.dataset.value ? 'true' : 'false'
          );
        });
    }
  }

  renderMedia(format, variant) {
    // Preview media is keyed by Shopify media ID — never by alt text and
    // never by array order.
    let mediaId = variant && variant.mediaId;

    // Photograph override: for the formats named in the section settings,
    // show the photograph itself. Those variants' own media are dimension
    // diagrams rather than print renders. A presentation patch over a
    // content fault — remove this once PC-9 normalises preview media at
    // generation.
    if (this.photoFormats.includes(format) && this.data.photoMediaId) {
      mediaId = this.data.photoMediaId;
    }

    const media = mediaId && this.data.media[String(mediaId)];
    if (media && this.image) {
      this.image.src = media.src;
      this.image.alt = media.alt;
    }

    const formatLabel = this.querySelector('[data-tpp-wellformat]');
    const sizeLabel = this.querySelector('[data-tpp-wellsize]');
    if (formatLabel) formatLabel.textContent = this.labelFor(format);
    if (sizeLabel) sizeLabel.textContent = this.state[2] || '';

    this.renderThumbs(format, mediaId);
  }

  renderThumbs(format, activeMediaId) {
    if (!this.thumbsHost) return;

    const ids = [];
    this.data.variants
      .filter((v) => v.options[0] === format)
      .forEach((v) => {
        if (v.mediaId && !ids.includes(v.mediaId)) ids.push(v.mediaId);
      });

    // A single preview is not a gallery — don't render a lone thumbnail.
    if (ids.length < 2) {
      this.thumbsHost.replaceChildren();
      return;
    }

    this.thumbsHost.replaceChildren(
      ...ids.map((id) => {
        const media = this.data.media[String(id)];
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'tpp__thumb';
        btn.setAttribute('aria-pressed', id === activeMediaId ? 'true' : 'false');
        btn.setAttribute('aria-label', (media && media.alt) || 'Preview');

        const img = document.createElement('img');
        img.src = (media && media.src) || '';
        img.alt = '';
        img.loading = 'lazy';
        btn.appendChild(img);

        // Selecting a thumbnail selects the size that owns it, so the image
        // and the chosen variant can never disagree.
        btn.addEventListener('click', () => {
          const owner = this.data.variants.find(
            (v) => v.options[0] === format && v.mediaId === id
          );
          if (!owner) return;
          owner.options.forEach((value, i) => {
            this.state[i + 1] = value;
          });
          this.render();
        });

        return btn;
      })
    );
  }

  renderPurchase(format, variant) {
    const buyTitle = this.querySelector('[data-tpp-buytitle]');
    if (buyTitle) buyTitle.textContent = this.labelFor(format);

    // Fail-closed: a format with no purchasable combination. A guard, not a
    // routine state — given the never-sold-out invariant, reaching it means
    // the product data is wrong.
    const purchasable = this.data.variants.some(
      (v) => v.options[0] === format && v.available !== false
    );
    const closed = !purchasable || !variant;

    if (this.closedNotice) this.closedNotice.hidden = !closed;
    if (this.productForm) this.productForm.hidden = closed;

    const stock = this.querySelector('[data-tpp-stock]');
    if (stock) {
      stock.innerHTML = closed
        ? ''
        : 'Made to order — <strong>every print built for you</strong>';
    }

    if (this.priceNode && variant) {
      this.priceNode.innerHTML = variant.priceFormatted;
    }

    if (this.variantInput) {
      this.variantInput.value = variant ? variant.id : '';
      this.variantInput.disabled = !variant;
    }

    if (this.submit) this.submit.disabled = closed;

    // Keep the address bar in step so the page can be shared or reloaded on
    // the chosen variant. replaceState, not pushState: a format change is not
    // a navigation and should not fill the back button.
    if (variant && window.history && window.history.replaceState) {
      const url = new URL(window.location.href);
      url.searchParams.set('variant', variant.id);
      window.history.replaceState({}, '', url.toString());
    }
  }
}

if (!customElements.get('mom-product-photo')) {
  customElements.define('mom-product-photo', TabbedPhotosPage);
}
